/**
 * Универсальный компонент динамического поля карточки.
 *
 * Рендерит UI-элемент в зависимости от field_type шаблона.
 */

import { memo, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useKlsOptions } from "@/hooks/useKlsOptions";
import type {
  CardTemplateField,
  CardFieldType,
  CardValuePayload,
  ObjectCardFieldValue,
} from "@/types/card-template";
import { CardScalarControl } from "@/components/card/shared";
import { GarAddressField } from "@/components/GarAddressField";

interface DynamicFieldProps {
  field: CardTemplateField;
  value: ObjectCardFieldValue | null;
  onChange: (fieldKey: string, payload: Partial<CardValuePayload>) => void;
  error?: string | null;
  readonly?: boolean;
  /** Текущий GUID ГАР объекта (из ObjectCardResponse.gar_uuid) */
  objectGarUuid?: string | null;
}

export const DynamicField = memo(function DynamicField({
  field,
  value,
  onChange,
  error,
  readonly = false,
  objectGarUuid = null,
}: DynamicFieldProps) {
  const isDisabled = readonly || field.is_readonly;
  const firstVal = value?.values?.[0];
  const isObjAddress = field.field_key === "obj_address";
  const effectiveGarUuid =
    firstVal && "gar_uuid" in firstVal
      ? (firstVal.gar_uuid ?? null)
      : (objectGarUuid ?? null);

  const handleChange = useCallback(
    (partial: Partial<CardValuePayload>) => {
      onChange(field.field_key, {
        field_key: field.field_key,
        qual_id: field.qual_id,
        kls_id: field.kls_id,
        ...partial,
      });
    },
    [onChange, field.field_key, field.qual_id, field.kls_id]
  );

  if (isObjAddress) {
    return (
      <div className="space-y-1.5">
        <GarAddressField
          label={
            field.is_required ? `${field.label} *` : field.label || "Адрес"
          }
          address={firstVal?.value_text ?? value?.display_value ?? ""}
          garUuid={effectiveGarUuid}
          onAddressChange={(text) => handleChange({ value_text: text })}
          onGarUuidChange={(uuid, formatted) =>
            handleChange({ value_text: formatted, gar_uuid: uuid })
          }
          className={isDisabled ? "pointer-events-none opacity-60" : undefined}
        />
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 flex-wrap">
        <TooltipProvider delayDuration={300}>
          <Label
            htmlFor={field.field_key}
            className={error ? "text-destructive" : ""}
          >
            {field.label}
            {field.is_required && <span className="text-destructive ml-0.5">*</span>}
          </Label>
          {field.description && (
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help shrink-0" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                {field.description}
              </TooltipContent>
            </Tooltip>
          )}
          {value?.inherited_from_parent_object ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  variant="secondary"
                  className="text-[10px] px-1.5 py-0 font-normal border border-muted-foreground/25 shrink-0"
                >
                  от родителя
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                Значение подставлено с родительского объекта (цепочка parent_object_id).
              </TooltipContent>
            </Tooltip>
          ) : null}
        </TooltipProvider>
      </div>

      <FieldInput
        fieldType={field.field_type as CardFieldType}
        fieldKey={field.field_key}
        qualId={field.qual_id}
        value={firstVal}
        displayValue={value?.display_value}
        onChange={handleChange}
        disabled={!!isDisabled}
        validationRules={field.validation_rules}
        uiConfig={field.ui_config}
      />

      {error && (
        <p className="text-xs text-destructive">{error}</p>
      )}
    </div>
  );
});

export default DynamicField;

// ── Внутренний компонент для рендера по типу ─────────────────────────

/** Стандартное поле статуса объекта (core.objects.status) — всегда выбор, не текстовый ввод */
const OBJECT_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "active", label: "Активный" },
  { value: "inactive", label: "Неактивный" },
  { value: "archived", label: "Архивный" },
  { value: "pending", label: "Ожидает" },
];

interface FieldInputProps {
  fieldType: CardFieldType;
  fieldKey: string;
  qualId: number;
  value?: CardValuePayload | null;
  displayValue?: string | null;
  onChange: (partial: Partial<CardValuePayload>) => void;
  disabled: boolean;
  validationRules?: Record<string, unknown>;
  uiConfig?: Record<string, unknown>;
}

function FieldInput({
  fieldType,
  fieldKey,
  qualId,
  value,
  onChange,
  disabled,
  validationRules,
  uiConfig,
}: FieldInputProps) {
  // Строковый жизненный цикл core.objects.status. Классификатор STAT_OBJ идёт обычным KLS-виджетом.
  if (fieldKey === "obj_status" && (fieldType === "string" || fieldType === "text")) {
    return (
      <ObjectStatusSelect
        id={fieldKey}
        value={value}
        onChange={onChange}
        disabled={disabled}
        optionsFromUi={
          Array.isArray(uiConfig?.status_options)
            ? (uiConfig.status_options as { value: string; label: string }[])
            : undefined
        }
      />
    );
  }

  switch (fieldType) {
    case "string":
      return (
        <CardScalarControl
          fieldType="string"
          id={fieldKey}
          value={value?.value_text ?? ""}
          onChange={(next) => onChange({ value_text: next as string })}
          disabled={disabled}
          validationRules={validationRules}
          booleanVariant="switch"
        />
      );

    case "text":
      return (
        <CardScalarControl
          fieldType="text"
          id={fieldKey}
          value={value?.value_text ?? ""}
          onChange={(next) => onChange({ value_text: next as string })}
          disabled={disabled}
          validationRules={validationRules}
          rowsText={4}
          booleanVariant="switch"
        />
      );

    case "number":
      return (
        <CardScalarControl
          fieldType="number"
          id={fieldKey}
          value={value?.value_numeric ?? ""}
          onChange={(next) =>
            onChange({
              value_numeric: next === null || next === undefined ? null : Number(next),
            })
          }
          disabled={disabled}
          validationRules={validationRules}
          booleanVariant="switch"
        />
      );

    case "date":
      return (
        <CardScalarControl
          fieldType="date"
          id={fieldKey}
          value={value?.value_date ?? ""}
          onChange={(next) => onChange({ value_date: (next as string | null) || null })}
          disabled={disabled}
          booleanVariant="switch"
        />
      );

    case "boolean":
      return (
        <CardScalarControl
          fieldType="boolean"
          id={fieldKey}
          value={value?.value_boolean ?? false}
          onChange={(next) => onChange({ value_boolean: Boolean(next) })}
          disabled={disabled}
          booleanVariant="switch"
        />
      );

    case "json":
      return (
        <CardScalarControl
          fieldType="json"
          id={fieldKey}
          value={value?.value_json}
          onChange={(next) => {
            if (typeof next === "string") {
              onChange({ value_text: next });
            } else {
              onChange({
                value_json: next as Record<string, unknown> | null,
              });
            }
          }}
          disabled={disabled}
          rowsJson={5}
          booleanVariant="switch"
        />
      );

    case "classifier":
    case "classifier_tree":
      return (
        <ClassifierSelect
          qualId={qualId}
          value={value?.kls_id}
          onChange={(klsId) =>
            onChange(
              fieldKey === "obj_status"
                ? { kls_id: klsId, value_text: null }
                : { kls_id: klsId },
            )
          }
          disabled={disabled}
        />
      );

    case "file":
      return (
        <p className="text-sm text-muted-foreground italic pt-1">
          Управление файлами — во вкладке «Файлы»
        </p>
      );

    case "geometry_link":
      return (
        <p className="text-sm text-muted-foreground italic pt-1">
          Ссылка на геометрию — настраивается автоматически
        </p>
      );

    default:
      return (
        <Input
          id={fieldKey}
          value={value?.value_text ?? ""}
          onChange={(e) => onChange({ value_text: e.target.value })}
          disabled={disabled}
        />
      );
  }
}

function ObjectStatusSelect({
  id,
  value,
  onChange,
  disabled,
  optionsFromUi,
}: {
  id: string;
  value?: CardValuePayload | null;
  onChange: (partial: Partial<CardValuePayload>) => void;
  disabled: boolean;
  optionsFromUi?: { value: string; label: string }[];
}) {
  const base = optionsFromUi?.length ? optionsFromUi : OBJECT_STATUS_OPTIONS;
  const raw = (value?.value_text ?? "active").trim() || "active";
  const known = base.some((o) => o.value === raw);
  const options = known
    ? base
    : [...base, { value: raw, label: raw }];

  return (
    <Select
      value={raw}
      onValueChange={(v) => onChange({ value_text: v })}
      disabled={disabled}
    >
      <SelectTrigger id={id} className="h-9 w-full">
        <SelectValue placeholder="Статус" />
      </SelectTrigger>
      <SelectContent className="z-[9999]">
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ── Classifier Select ────────────────────────────────────────────────

function ClassifierSelect({
  qualId,
  value,
  onChange,
  disabled,
}: {
  qualId: number;
  value?: number | null;
  onChange: (klsId: number | null) => void;
  disabled: boolean;
}) {
  const { data: options, isLoading } = useKlsOptions(qualId);
  const comboboxOptions: ComboboxOption[] = (options ?? []).map((opt) => ({
    value: String(opt.value),
    label: opt.label,
    shortLabel: opt.shortLabel,
    fullLabel: opt.fullLabel,
    indentLevel: opt.indentLevel ?? 0,
  }));

  return (
    <Combobox
      options={comboboxOptions}
      value={value != null && value > 0 ? String(value) : undefined}
      onValueChange={(v) => onChange(v ? Number(v) : null)}
      placeholder={isLoading ? "Загрузка..." : "Выберите значение"}
      searchPlaceholder="Поиск..."
      emptyText="Ничего не найдено"
      disabled={disabled || isLoading}
      allowClear
      clearText="Выберите значение"
      className="h-9"
    />
  );
}
